import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import CalendlyEmbed from "../components/CalendlyEmbed";
import {
  Calendar,
  Clock,
  CheckCircle,
  Phone,
  Video,
  MessageSquare,
} from "lucide-react";
import SEO from "../components/SEO";
import StructuredData from "../components/StructuredData";

const Consultation = () => {
  const benefits = [
    {
      icon: CheckCircle,
      title: "No Pressure",
      description: "Free 30-minute chat with no obligation—just helpful advice",
    },
    {
      icon: Video,
      title: "Meet Virtually",
      description: "Connect via Zoom from wherever you are—no travel needed",
    },
    {
      icon: MessageSquare,
      title: "Real Advice",
      description: "Get personalized guidance from people who know healthcare",
    },
  ];

  const whatToExpect = [
    "Discussion of your practice goals and challenges",
    "Overview of services that match your needs",
    "Honest assessment of how we can help",
    "Clear next steps and recommendations",
    "Answers to all your questions",
  ];

  return (
    <div>
      <SEO 
        title="Free Consultation - Talk to a Healthcare Operations Expert"
        description="Book a free 30-minute call to discuss your clinic's challenges — whether it's staffing issues, workflow bottlenecks, compliance concerns, or growth plans. No obligation, just honest guidance."
        keywords="free healthcare consultation, talk to practice consultant, medical practice help call, clinic operations consultation, healthcare advisor free call, practice management advice"
      />
      <StructuredData data={[{
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.rnbconsultinggroup.com" },
          { "@type": "ListItem", "position": 2, "name": "Free Consultation", "item": "https://www.rnbconsultinggroup.com/consultation" }
        ]
      }]} />
      
      {/* Hero Section */}
      <section className="relative py-16 bg-gradient-to-br from-rnb-light to-white overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-96 h-96 bg-rnb-blue/10 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="inline-flex items-center px-4 py-2 bg-rnb-blue/10 rounded-full mb-6">
              <Calendar className="w-4 h-4 text-rnb-blue mr-2" />
              <span className="text-sm font-medium text-rnb-navy">Free Consultation</span>
            </div>

            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-rnb-navy mb-6">
              Let&apos;s Have a Conversation
            </h1>

            <p className="text-lg text-rnb-gray">
              Not sure where to start? Book a free 30-minute call. We&apos;ll listen to 
              your challenges, share some ideas, and see if we&apos;re the right fit—no strings attached.
            </p>
          </div>

          {/* Benefits */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {benefits.map((benefit, index) => (
              <Card key={benefit.title || benefit.name || i} className="border-0 shadow-sm">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-rnb-blue/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <benefit.icon className="w-6 h-6 text-rnb-blue" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-rnb-navy mb-2">
                    {benefit.title}
                  </h3>
                  <p className="text-sm text-rnb-gray">{benefit.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Calendly Embed Section */}
      <section className="py-12 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Sidebar */}
            <div className="lg:col-span-1">
              <Card className="border-0 shadow-sm sticky top-8">
                <CardContent className="p-6">
                  <h3 className="font-heading text-lg font-semibold text-rnb-navy mb-4">
                    What to Expect
                  </h3>
                  <ul className="space-y-3">
                    {whatToExpect.map((item, index) => (
                      <li key={item.title || item.name || item.label || index} className="flex items-start gap-2 text-sm text-rnb-gray">
                        <CheckCircle className="w-4 h-4 text-rnb-teal flex-shrink-0 mt-0.5" />
                        {item}
                      </li>
                    ))}
                  </ul>

                  <div className="border-t mt-6 pt-6">
                    <div className="flex items-center gap-3 text-sm text-rnb-gray mb-3">
                      <Clock className="w-4 h-4" />
                      <span>30 minutes</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-rnb-gray mb-3">
                      <Video className="w-4 h-4" />
                      <span>Zoom video call</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm text-rnb-gray">
                      <Phone className="w-4 h-4" />
                      <span>Phone option available</span>
                    </div>
                  </div>

                  <div className="border-t mt-6 pt-6">
                    <p className="text-sm text-rnb-gray mb-4">
                      Prefer to reach out directly?
                    </p>
                    <Link to="/contact">
                      <Button variant="outline" size="sm" className="w-full">
                        <MessageSquare className="mr-2 h-4 w-4" />
                        Contact Us
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Calendly Widget */}
            <div className="lg:col-span-2">
              <Card className="border-0 shadow-sm overflow-hidden">
                <CardContent className="p-0">
                  <CalendlyEmbed 
                    url="https://calendly.com/rnb-consulting-group/free-discovery-call"
                    height="700px"
                  />
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Alternative CTA */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl font-bold text-rnb-navy mb-4">
            Ready to Get Started Right Away?
          </h2>
          <p className="text-rnb-gray mb-8">
            If you already know what you need, explore our signature programs and enroll directly.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/programs/foundations-first">
              <Button variant="outline" size="lg">
                Foundations First – $1,250
              </Button>
            </Link>
            <Link to="/programs/carerevive">
              <Button variant="outline" size="lg">
                CareRevive – $750
              </Button>
            </Link>
            <Link to="/programs/concierge-frontline">
              <Button variant="outline" size="lg">
                Concierge Frontline – $2,200
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Consultation;
